'use client';

import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useOrganizationStore from '@/state/orgState';
import getStripeCheckoutUrl from '@/apis/getStripeCheckoutUrl';
import getCustomerPortalUrl from '@/apis/getCustomerPortalUrl';
import headerObject from '@/types/headerObj';
import emptyHeaderObj from '@/types/emptyHeaderObj';
import { useOrganizationUsers } from '@/hooks/useOrganizationUsers';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import useBillingStore from '@/state/billingState';
import getDateFromUnixEpochTimeStamp from '@/helpers/getDateFromUnixEpochTimeStamp';
import { CheckCircle, AlertTriangle, ChevronDown, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useDisclosure } from '@/hooks/useDisclosure';
import { useToast } from '@/hooks/useToast';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import User from '@/types/userType';
import { useUserLogged } from '@/hooks/useUserLogged';
import { isOwner, isAdminOrOwner, roleLabel, ownerOnly } from '@/lib/rbac';
import { useSession } from 'next-auth/react';
import { useInvoices } from '@/hooks/useInvoices';
import { usePricing } from '@/hooks/usePricing';
import { useRoleChangeSeatManagement } from '@/hooks/useRoleChangeSeatManagement';
import { useTeamData, useAllUsers } from '@/hooks/useTeamData';
import { AddMemberModal } from '../UserManagement/Modals/AddMemberModal';
import { SettingsHeader } from '../Settings/components/SettingsHeader';
import { BillingLlmUsageSummary } from '@/components/Profile/BillingLlmUsageSummary';
import { BillingModelRouterSection } from '@/components/Profile/BillingModelRouterSection';
import { BillingTierPlansSection } from '@/components/Profile/BillingTierPlansSection';
import { BILLING_PLAN_TIERS } from '@/constants/billingPlanTiers';

function formatUsdMonthly(n: number): string {
  if (n <= 0) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

// Define Team type for teams with members
type TeamWithMembers = {
  TeamName: string;
  TeamUUID: string;
  TeamMembers: Array<{
    avatar_url?: string;
    login: string;
  }>;
};

type BillingState = {
  searchTerm: string;
  modalType: string;
  selectedRole: string;
};

type BillingAction =
  | { type: 'SET_SEARCH_TERM'; payload: string }
  | { type: 'SET_MODAL_TYPE'; payload: string }
  | { type: 'SET_SELECTED_ROLE'; payload: string };

function billingReducer(
  state: BillingState,
  action: BillingAction
): BillingState {
  switch (action.type) {
    case 'SET_SEARCH_TERM':
      return { ...state, searchTerm: action.payload };
    case 'SET_MODAL_TYPE':
      return { ...state, modalType: action.payload };
    case 'SET_SELECTED_ROLE':
      return { ...state, selectedRole: action.payload };
    default:
      return state;
  }
}

const initialBillingState: BillingState = {
  searchTerm: '',
  modalType: '',
  selectedRole: 'all',
};

function BillingContent() {
  const billStore = useBillingStore();
  const { organization } = useOrganizationStore();
  const { user } = useUserLogged();
  const [state, dispatch] = useReducer(billingReducer, initialBillingState);
  const { searchTerm, modalType, selectedRole } = state;
  const params = useSearchParams();
  const stripeStatus = params?.get('status') ?? null;

  const { data: session } = useSession();

  // Use React Query hooks instead of manual fetching
  const { data: organizationUsersData } = useOrganizationUsers();
  const { data: trialStatus, isLoading: isTrialStatusLoading } = useTrialStatus(
    user?.UserUUID,
    !!user?.UserUUID
  );

  // Get org users from React Query (already filtered with git usernames)
  const orgUsersFromQuery = organizationUsersData || [];

  // Modal state for AddMemberModal
  const {
    isOpen: addMemberIsOpen,
    onOpen: addMemberOnOpen,
    onClose: addMemberOnClose,
  } = useDisclosure();

  const {
    isOpen: isStatusModalOpen,
    onOpen: onStatusModalOpen,
    onClose: onStatusModalClose,
  } = useDisclosure();

  const {
    isOpen: isCancelModalOpen,
    onOpen: onCancelModalOpen,
    onClose: onCancelModalClose,
  } = useDisclosure();

  const router = useRouter();
  const toast = useToast();

  const {
    data: invoices,
    isLoading: loadingInvoices,
    error: invoicesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInvoices(
    organization?.OrgUUID
      ? {
          OrganizationUUID: organization.OrgUUID,
          Limit: 5,
        }
      : null,
    user,
    organization
  );

  const {
    data: pricing,
    isLoading: loadingPricing,
    error: pricingError,
  } = usePricing();

  const roleChangeMutation = useRoleChangeSeatManagement();

  // Create header object for API calls (memoized to prevent unnecessary re-renders)
  const headerObj: headerObject = useMemo(
    () => ({
      ...emptyHeaderObj,
      Useruuid: user?.UserUUID,
      Useremail: user?.Email,
      Username: user?.FirstName,
      Orgname: organization?.OrgName,
      Orguuid: organization?.OrgUUID,
    }),
    [
      user?.UserUUID,
      user?.Email,
      user?.FirstName,
      organization?.OrgName,
      organization?.OrgUUID,
    ]
  );

  // Get full user list using React Query (all users, not just git users)
  const { data: allUsersData = [], isLoading: isLoadingAllUsers } = useAllUsers(
    organization?.OrgUUID,
    headerObj
  );
  const orgArr = allUsersData || [];

  // Get teams with members data
  const { teams: teamsQuery, isLoading: loadingTeams } = useTeamData(
    organization?.OrgUUID,
    headerObj
  );
  const teams = teamsQuery.data || [];

  useEffect(() => {
    billStore.getPlanDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once fetch; billStore is the whole store object and changes reference on every state update, so adding it would loop
  }, []);

  const createStripeCheckoutSession = async () => {
    if (
      billStore.status === 'active' ||
      billStore.status === 'cancelled' ||
      !user ||
      !organization
    ) {
      return;
    }

    try {
      const stripeCheckoutUrl = await getStripeCheckoutUrl(
        organization,
        user as User,
        headerObj,
        billStore.billingInterval
      );
      if (stripeCheckoutUrl?.CheckoutUrl) {
        const url = stripeCheckoutUrl.CheckoutUrl;
        const newWindow = window.open();
        if (newWindow) newWindow.location.href = url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
  };

  const cancelSubscription = async () => {
    billStore.cancelSubscription();
    onCancelModalClose();
  };

  const manageSubscription = async () => {
    if (!organization || !user) {
      return;
    }

    try {
      const portalResponse = await getCustomerPortalUrl(
        organization,
        headerObj
      );

      if (portalResponse?.PortalUrl) {
        window.open(portalResponse.PortalUrl, '_blank');
      } else {
        const errorMessage =
          portalResponse?.Error || 'Failed to open subscription management';
        toast({
          title: 'Subscription Management Unavailable',
          description: errorMessage.includes('not configured')
            ? 'Subscription management is currently being set up. Please try again later or contact support.'
            : errorMessage,
          status: 'error',
          duration: 7000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: 'Error',
        description: 'Failed to open subscription management',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleRoleChangeClick = async (targetUser: User, newRole: string) => {
    // Only Owners can change roles here
    if (!isOwner(user?.Role)) {
      toast({
        title: 'Access Denied',
        description: `${ownerOnly('change user roles')}.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Prevent users from changing their own role
    if (user?.UserUUID === targetUser.UserUUID) {
      toast({
        title: 'Cannot Change Own Role',
        description:
          'You cannot change your own role. Ask another administrator to do this.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Don't change if already the same role
    if (targetUser.Role === newRole) {
      return;
    }

    try {
      await roleChangeMutation.mutateAsync({
        organizationUUID: organization?.OrgUUID || '',
        userUUID: targetUser.UserUUID,
        oldRole: targetUser.Role,
        newRole: newRole,
      });

      // Refresh the user list to show updated roles
      refetchAllUsers();
    } catch (error) {
      // Error handling is done in the React Query hook
      console.error('Error changing role:', error);
    }
  };

  const pathname = usePathname();
  const showSuccessModal =
    stripeStatus === 'success' ||
    (isStatusModalOpen && modalType === 'success');
  const showFailureModal =
    stripeStatus === 'failure' ||
    (isStatusModalOpen && modalType === 'failure');
  const handleStatusModalOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onStatusModalClose();
        if (stripeStatus && pathname) {
          router.replace(pathname);
        }
      }
    },
    [onStatusModalClose, stripeStatus, pathname, router]
  );

  // Refetch function for manual refresh (e.g., after role change)
  const queryClient = useQueryClient();
  const refetchAllUsers = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['users', 'all', organization?.OrgUUID],
    });
  }, [queryClient, organization?.OrgUUID]);

  const formatInvoiceDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatInvoiceAmount = (amount: number) => {
    return `$${(amount / 100).toFixed(0)}`;
  };

  const downloadInvoice = (invoicePdf: string) => {
    if (invoicePdf) {
      window.open(invoicePdf, '_blank');
    }
  };

  // Filter users based on search term and selected role
  const filteredUsers = orgArr.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    const fullName =
      `${user.FirstName || ''} ${user.LastName || ''}`.toLowerCase();
    const email = user.Email?.toLowerCase() || '';
    const githubUsername = user.GithubUsername?.toLowerCase() || '';
    const gitlabUsername = user.GitlabUsername?.toLowerCase() || '';

    // Apply search filter
    const matchesSearch =
      fullName.includes(searchLower) ||
      email.includes(searchLower) ||
      githubUsername.includes(searchLower) ||
      gitlabUsername.includes(searchLower);

    // Apply role filter
    const matchesRole =
      selectedRole === 'all' ||
      (selectedRole === 'admin' && isAdminOrOwner(user.Role)) ||
      (selectedRole === 'member' && user.Role === 'member');

    return matchesSearch && matchesRole;
  });

  // Calculate pricing based on roles using dynamic pricing
  const adminCount = orgArr.filter((user) => isAdminOrOwner(user.Role)).length;
  const memberCount = orgArr.filter((user) => user.Role === 'member').length;

  // Use dynamic pricing based on actual subscription interval OR selected billing interval for inactive subs
  const isAnnualBilling =
    billStore.status === 'active'
      ? billStore.interval === 'year'
      : billStore.billingInterval === 'year';

  const adminSeatPrice = isAnnualBilling
    ? pricing?.admin_seat_price_annual || 600
    : pricing?.admin_seat_price || 60;
  const memberSeatPrice = isAnnualBilling
    ? pricing?.member_seat_price_annual || 360
    : pricing?.member_seat_price || 40;

  const adminCost = adminCount * adminSeatPrice;
  const memberCost = memberCount * memberSeatPrice;
  const totalCost = adminCost + memberCost;

  const currentTier = billStore.planDetailsLoaded
    ? (BILLING_PLAN_TIERS.find(
        (t) => t.id === (billStore.planTier || 'free').toLowerCase()
      ) ?? BILLING_PLAN_TIERS[0])
    : null;

  const orgUuidForUsage =
    (organization?.OrgUUID && organization.OrgUUID.trim()) ||
    (user?.OrganizationUUID && user.OrganizationUUID.trim()) ||
    '';

  return (
    <div className="pb-12 motion-stagger">
      {/* Custom Header with Manage Subscription Button */}
      <div className="mb-11">
        <div className="flex justify-between items-start mb-2">
          <SettingsHeader
            title="Billing"
            description="Manage your subscription and billing settings."
            marginBottom={0}
            classes="mb-0"
          />
          {/* Manage Subscription Button - Only show for active subscriptions.
              getCustomerPortalUrl is Owner-only on the backend
              (`require_owner_scope`); hide the control for anyone else
              instead of surfacing a 403. */}
          {billStore.status === 'active' && isOwner(user?.Role) && (
            <Button
              variant="secondary"
              size="sm"
              className="flex items-center gap-1.5"
              onClick={manageSubscription}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="15"
                viewBox="0 0 14 15"
                fill="none"
              >
                <path
                  d="M8.75 2.5H12.25V6"
                  stroke="currentColor"
                  strokeWidth="1.16667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.83203 8.91667L12.2487 2.5"
                  stroke="currentColor"
                  strokeWidth="1.16667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.5 8.33333V11.8333C10.5 12.1428 10.3771 12.4395 10.1583 12.6583C9.9395 12.8771 9.64275 13 9.33333 13H2.91667C2.60725 13 2.3105 12.8771 2.09171 12.6583C1.87292 12.4395 1.75 12.1428 1.75 11.8333V5.41667C1.75 5.10725 1.87292 4.8105 2.09171 4.59171C2.3105 4.37292 2.60725 4.25 2.91667 4.25H6.41667"
                  stroke="currentColor"
                  strokeWidth="1.16667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Manage Subscription
            </Button>
          )}
        </div>
      </div>

      {orgUuidForUsage && isOwner(user?.Role) && (
        <BillingLlmUsageSummary
          organizationUUID={orgUuidForUsage}
          planTokensIncluded={billStore.tokensIncluded || undefined}
        />
      )}

      <BillingModelRouterSection />

      <div className="grid grid-cols-1 lg:grid-cols-5 border border-ds-border-subtlest rounded-lg min-h-[480px] lg:h-[480px]">
        <div className="lg:col-span-2 rounded-t-lg lg:rounded-t-none lg:rounded-l-lg flex flex-col border-b border-ds-border-subtlest bg-ds-surface-white">
          {/* Left Column - Subscription */}
          <div className="p-4 md:p-6 flex flex-col">
            <div>
              <div>
                <p className="ds-text-title-sm-semibold text-ds-text-darker mb-1">
                  Cost Summary
                </p>
                <p className="ds-text-body-sm-medium text-ds-text-subtle mb-4">
                  Current plan breakdown.
                </p>
              </div>

              {!currentTier ? (
                <div className="mt-4 space-y-4 animate-pulse">
                  <div className="h-4 bg-ds-gray-subtler rounded w-3/4" />
                  <div className="h-4 bg-ds-gray-subtler rounded w-1/2" />
                  <div className="h-4 bg-ds-gray-subtler rounded w-2/3" />
                  <div className="border-t border-ds-border-subtlest" />
                  <div className="h-4 bg-ds-gray-subtler rounded w-1/2" />
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  {/* Plan tier */}
                  <div>
                    <div className="flex justify-between items-start">
                      <span className="ds-text-body-sm-semibold text-ds-text-darker">
                        {currentTier.name} Plan
                      </span>
                      <span className="ds-text-body-sm-semibold text-ds-text-darker tabular-nums">
                        {currentTier.contactSales
                          ? 'Custom'
                          : formatUsdMonthly(currentTier.priceMonthlyUsd)}
                      </span>
                    </div>
                    <span className="ds-text-body-xs-medium text-ds-text-subtle">
                      {currentTier.headcountLabel}
                    </span>
                  </div>

                  {/* Tokens included */}
                  <div className="flex justify-between items-start">
                    <span className="ds-text-body-sm-medium text-ds-text-default">
                      Tokens included
                    </span>
                    <span className="ds-text-body-sm-medium text-ds-text-default tabular-nums text-right">
                      {currentTier.tokensSummary}{' '}
                      <span className="ds-text-body-xs-medium text-ds-text-subtle">
                        {currentTier.tokensDetail}
                      </span>
                    </span>
                  </div>

                  {/* Overage rate */}
                  <div className="flex justify-between items-start">
                    <span className="ds-text-body-sm-medium text-ds-text-default">
                      Overage rate
                    </span>
                    <span className="ds-text-body-sm-medium text-ds-text-subtle tabular-nums">
                      $5 / 1M tokens
                    </span>
                  </div>

                  {/* Separator */}
                  <div className="border-t border-ds-border-subtlest" />

                  {/* Next Bill Total */}
                  <div>
                    <div className="flex justify-between items-start mb-1">
                      <span className="ds-text-body-sm-semibold text-ds-text-darker">
                        Next Bill Total
                      </span>
                      <span className="ds-text-body-sm-semibold text-ds-text-darker tabular-nums">
                        {currentTier.contactSales
                          ? 'Custom'
                          : formatUsdMonthly(currentTier.priceMonthlyUsd)}
                      </span>
                    </div>
                    <p className="ds-text-body-xs-medium text-ds-text-subtle">
                      {billStore.status === 'active' &&
                      billStore.nextBillingDate > 0
                        ? `Billed ${billStore.interval === 'year' ? 'annually' : 'monthly'} on ${getDateFromUnixEpochTimeStamp(billStore.nextBillingDate)}`
                        : currentTier.id === 'free'
                          ? 'No payment required'
                          : `Billed ${billStore.interval === 'year' ? 'annually' : 'monthly'}`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Organization Members */}
        <div className="lg:col-span-3 bg-ds-surface-off-white p-4 md:p-6 flex flex-col min-h-[400px] lg:h-[480px]">
          <div className="flex flex-col md:flex-row justify-between md:items-center gap-3 md:gap-0 mb-4 md:mb-6">
            <div>
              <p className="ds-text-title-sm-semibold text-ds-text-darker mb-1">
                Team Management
              </p>
              <p className="ds-text-body-sm-medium text-ds-text-subtle">
                {orgArr.length} billed seats
              </p>
            </div>
            <div className="flex gap-3">
              {!billStore.gitSynced && (
                <Button
                  variant="outline"
                  className="flex items-center gap-2"
                  onClick={() => router.push('/settings?tab=team')}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 10 10"
                    fill="none"
                  >
                    <path
                      d="M0.78125 3.875H3.59375C3.66992 3.875 3.73584 3.84717 3.7915 3.7915C3.84717 3.73584 3.875 3.66992 3.875 3.59375C3.875 3.51172 3.8457 3.44141 3.78711 3.38281C3.72852 3.32422 3.66992 3.26563 3.61133 3.20703L2.8291 2.4248C3.12207 2.17871 3.45312 1.98389 3.82227 1.84033C4.19141 1.69678 4.58398 1.625 5 1.625C5.41602 1.625 5.81152 1.69678 6.18652 1.84033C6.56153 1.98389 6.89551 2.18164 7.18848 2.43359C7.48145 2.68555 7.72754 2.98291 7.92676 3.32568C8.12598 3.66846 8.25781 4.03906 8.32227 4.4375H9.46484C9.39453 3.88086 9.23047 3.36377 8.97266 2.88623C8.71484 2.40869 8.38672 1.99268 7.98828 1.63818C7.58984 1.28369 7.13574 1.00537 6.62598 0.803223C6.11621 0.601073 5.57422 0.5 5 0.5C4.71289 0.5 4.43457 0.526367 4.16504 0.579102C3.89551 0.631836 3.63477 0.706543 3.38281 0.803223C3.13086 0.899903 2.89209 1.01709 2.6665 1.15479C2.44092 1.29248 2.22852 1.44922 2.0293 1.625L1.2207 0.825195C1.14453 0.749023 1.07129 0.675782 1.00098 0.605469C0.930664 0.535156 0.857422 0.5 0.78125 0.5C0.705078 0.5 0.639161 0.526367 0.583496 0.579102C0.527832 0.631836 0.5 0.699218 0.5 0.78125V3.59375C0.5 3.61133 0.511719 3.6626 0.535156 3.74756C0.558594 3.83252 0.640624 3.875 0.78125 3.875ZM9.21875 6.125H6.40625C6.33008 6.125 6.26416 6.15283 6.2085 6.2085C6.15283 6.26416 6.125 6.33008 6.125 6.40625C6.125 6.48828 6.1543 6.55859 6.21289 6.61719C6.27148 6.67578 6.33008 6.73437 6.38867 6.79297L7.1709 7.5752C6.87793 7.82129 6.54688 8.01611 6.17773 8.15967C5.80859 8.30322 5.41602 8.375 5 8.375C4.58398 8.375 4.18848 8.30322 3.81348 8.15967C3.43847 8.01611 3.10449 7.81836 2.81152 7.56641C2.51855 7.31445 2.27246 7.01709 2.07324 6.67432C1.87402 6.33154 1.74219 5.96094 1.67773 5.5625H0.535156C0.605469 6.11914 0.76953 6.63623 1.02734 7.11377C1.28516 7.59131 1.61328 8.00732 2.01172 8.36182C2.41016 8.71631 2.86426 8.99463 3.37402 9.19678C3.88379 9.39893 4.42578 9.5 5 9.5C5.28711 9.5 5.56543 9.47363 5.83496 9.4209C6.10449 9.36816 6.36523 9.29346 6.61719 9.19678C6.86914 9.1001 7.10791 8.98291 7.3335 8.84522C7.55908 8.70752 7.77148 8.55078 7.9707 8.375L8.7793 9.1748C8.85547 9.25098 8.92871 9.32422 8.99902 9.39453C9.06934 9.46484 9.14258 9.5 9.21875 9.5C9.29492 9.5 9.36084 9.47363 9.4165 9.4209C9.47217 9.36816 9.5 9.30078 9.5 9.21875V6.40625C9.5 6.38867 9.48828 6.3374 9.46484 6.25244C9.44141 6.16748 9.35938 6.125 9.21875 6.125Z"
                      fill="currentColor"
                    />
                  </svg>
                  Sync Team
                </Button>
              )}
              <Button
                variant="default"
                className="flex items-center gap-2"
                onClick={addMemberOnOpen}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M8 3.33333V12.6667"
                    stroke="currentColor"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3.33334 8H12.6667"
                    stroke="currentColor"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Invite Member
              </Button>
            </div>
          </div>

          {/* Search Bar and Filter */}
          <div className="mb-4">
            <div className="flex gap-2 flex-row">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-ds-text-subtle">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                      stroke="currentColor"
                      strokeWidth="1.33333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14 14L11.1 11.1"
                      stroke="currentColor"
                      strokeWidth="1.33333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) =>
                    dispatch({
                      type: 'SET_SEARCH_TERM',
                      payload: e.target.value,
                    })
                  }
                  className="pl-10"
                />
              </div>

              {/* Team Filter Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 min-w-[100px] justify-between gap-1"
                    >
                      <span className="ds-text-body-sm-medium">
                        {selectedRole === 'all'
                          ? 'All Tiers'
                          : selectedRole === 'admin'
                            ? 'Admin'
                            : selectedRole === 'member'
                              ? 'Member'
                              : 'Select Tier'}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  }
                />
                <DropdownMenuContent align="start" className="min-w-[120px]">
                  <DropdownMenuItem
                    className={selectedRole === 'all' ? 'bg-accent' : ''}
                    onClick={() =>
                      dispatch({ type: 'SET_SELECTED_ROLE', payload: 'all' })
                    }
                  >
                    All Tiers
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={selectedRole === 'admin' ? 'bg-accent' : ''}
                    onClick={() =>
                      dispatch({ type: 'SET_SELECTED_ROLE', payload: 'admin' })
                    }
                  >
                    Admin
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={selectedRole === 'member' ? 'bg-accent' : ''}
                    onClick={() =>
                      dispatch({ type: 'SET_SELECTED_ROLE', payload: 'member' })
                    }
                  >
                    Member
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="space-y-3 overflow-y-auto flex-1 max-h-[300px] lg:max-h-none">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-8">
                <p className="ds-text-body-sm-medium text-ds-text-subtle">
                  {searchTerm
                    ? 'No users found matching your search'
                    : selectedRole !== 'all'
                      ? `No ${selectedRole} users found`
                      : 'No users found'}
                </p>
              </div>
            ) : (
              filteredUsers.map((item: User) => (
                <div
                  key={item.UserUUID}
                  className="bg-ds-surface-white rounded-lg p-4 border border-ds-border-subtlest hover:border-ds-border-subtler transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        {item.Image ? (
                          <AvatarImage
                            src={item.Image}
                            alt={`${item.FirstName || item.GithubUsername || 'User'} avatar`}
                            className="object-cover"
                          />
                        ) : null}
                        <AvatarFallback className="bg-ds-gray-subtler ds-text-body-sm-medium">
                          {item.FirstName?.charAt(0)?.toUpperCase() ||
                            item.GithubUsername?.charAt(0)?.toUpperCase() ||
                            'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="ds-text-body-sm-medium text-ds-text-darker">
                          {item.FirstName ||
                            item.GithubUsername ||
                            item.GitlabUsername}{' '}
                          {item.LastName}
                        </p>
                        <p className="ds-text-body-xs-medium text-ds-text-subtle">
                          {item.Email}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Role Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          render={
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={
                                !isOwner(user?.Role) ||
                                user?.UserUUID === item.UserUUID ||
                                (isOwner(item.Role) && !isOwner(user?.Role))
                              }
                              className="w-28"
                            >
                              {roleLabel(item.Role)}
                              <ChevronDown className="ml-1 h-3 w-3" />
                            </Button>
                          }
                        />
                        <DropdownMenuContent className="min-w-[120px]">
                          <DropdownMenuItem
                            disabled={
                              isAdminOrOwner(item.Role) ||
                              !isOwner(user?.Role) ||
                              user?.UserUUID === item.UserUUID ||
                              roleChangeMutation.isPending
                            }
                            onClick={() => handleRoleChangeClick(item, 'admin')}
                          >
                            {roleChangeMutation.isPending
                              ? 'Changing...'
                              : 'Admin'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={
                              item.Role === 'member' ||
                              !isOwner(user?.Role) ||
                              user?.UserUUID === item.UserUUID ||
                              roleChangeMutation.isPending
                            }
                            onClick={() =>
                              handleRoleChangeClick(item, 'member')
                            }
                          >
                            {roleChangeMutation.isPending
                              ? 'Changing...'
                              : 'Member'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      {/* More Options Button */}
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          render={
                            <Button
                              size="icon"
                              variant="ghost"
                              disabled={
                                !isOwner(user?.Role) ||
                                user?.UserUUID === item.UserUUID
                              }
                              className="w-8 h-8 rounded-full"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M8 6.66667C8.73638 6.66667 9.33333 6.06971 9.33333 5.33333C9.33333 4.59695 8.73638 4 8 4C7.26362 4 6.66667 4.59695 6.66667 5.33333C6.66667 6.06971 7.26362 6.66667 8 6.66667Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M8 8.66667C7.26362 8.66667 6.66667 9.26362 6.66667 10C6.66667 10.7364 7.26362 11.3333 8 11.3333C8.73638 11.3333 9.33333 10.7364 9.33333 10C9.33333 9.26362 8.73638 8.66667 8 8.66667Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M8 12.6667C7.26362 12.6667 6.66667 13.2636 6.66667 14C6.66667 14.7364 7.26362 15.3333 8 15.3333C8.73638 15.3333 9.33333 14.7364 9.33333 14C9.33333 13.2636 8.73638 12.6667 8 12.6667Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </Button>
                          }
                        />
                        <DropdownMenuContent className="min-w-[160px]">
                          <DropdownMenuItem
                            disabled={
                              !isOwner(user?.Role) ||
                              user?.UserUUID === item.UserUUID ||
                              roleChangeMutation.isPending
                            }
                            onClick={() =>
                              handleRoleChangeClick(
                                item,
                                isAdminOrOwner(item.Role) ? 'member' : 'admin'
                              )
                            }
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            {roleChangeMutation.isPending
                              ? 'Changing...'
                              : isAdminOrOwner(item.Role)
                                ? 'Downgrade to Member'
                                : 'Upgrade to Admin'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            disabled
                            className="ds-text-body-sm-medium text-ds-text-subtler"
                          >
                            Remove User (Coming Soon)
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {organization?.OrgUUID ? (
        <BillingTierPlansSection
          organizationUUID={organization.OrgUUID}
          headerObj={headerObj}
          isAdmin={isOwner(user?.Role)}
          currentPlanTier={billStore.planTier}
          subscriptionStatus={billStore.status}
          savedCardBrand={billStore.savedCardBrand}
          savedCardLast4={billStore.savedCardLast4}
          onPlansChanged={async () => {
            await billStore.getPlanDetails();
          }}
        />
      ) : null}

      {/* Invoice History Section */}
      <div className="mt-8 bg-ds-surface-white rounded-lg border border-ds-border-subtlest">
        <div className="p-4 md:p-6">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-lg bg-ds-gray-subtlest flex items-center justify-center text-ds-text-darker">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M10.9388 1.70837H4.3763C3.98953 1.70837 3.6186 1.86202 3.3451 2.13551C3.07161 2.409 2.91797 2.77993 2.91797 3.16671V14.8334C2.91797 15.2201 3.07161 15.5911 3.3451 15.8646C3.6186 16.1381 3.98953 16.2917 4.3763 16.2917H13.1263C13.5131 16.2917 13.884 16.1381 14.1575 15.8646C14.431 15.5911 14.5846 15.2201 14.5846 14.8334V5.35421L10.9388 1.70837Z"
                  stroke="currentColor"
                  strokeWidth="1.45833"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.207 1.70837V4.62504C10.207 5.01181 10.3607 5.38275 10.6342 5.65624C10.9077 5.92973 11.2786 6.08337 11.6654 6.08337H14.582"
                  stroke="currentColor"
                  strokeWidth="1.45833"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.29036 6.8125H5.83203"
                  stroke="currentColor"
                  strokeWidth="1.45833"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11.6654 9.72913H5.83203"
                  stroke="currentColor"
                  strokeWidth="1.45833"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11.6654 12.6459H5.83203"
                  stroke="currentColor"
                  strokeWidth="1.45833"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className="ds-text-title-sm-semibold text-ds-text-darker">
              Invoice History
            </span>
          </div>

          {loadingInvoices ? (
            <div className="flex justify-center py-8">
              <span className="ds-text-body-sm-medium text-ds-text-subtle">
                Loading invoices...
              </span>
            </div>
          ) : invoicesError ? (
            <div className="text-center py-8">
              <span className="ds-text-body-sm-medium text-ds-danger-default">
                Error loading invoices. Please try again.
              </span>
            </div>
          ) : invoices && invoices.length > 0 ? (
            <div className="space-y-3">
              {invoices.map((invoice, index) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-ds-gray-subtlest hover:bg-ds-brand-lightest transition-colors"
                >
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-3">
                      <span className="ds-text-body-sm-medium text-ds-text-darker">
                        {formatInvoiceDate(invoice.created)}
                      </span>
                      <div className="px-2 py-1 rounded-full bg-ds-gray-subtlest">
                        <span className="ds-text-body-xs-medium text-ds-text-darker">
                          {invoice.status === 'paid' ? 'Paid' : invoice.status}
                        </span>
                      </div>
                    </div>
                    <span className="ds-text-body-xs-medium text-ds-text-subtle">
                      {invoice.number || `Invoice ${index + 1}`}
                    </span>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="ds-text-body-md-medium text-ds-text-darker">
                      {formatInvoiceAmount(
                        invoice.amount_paid || invoice.total
                      )}
                    </span>
                    {invoice.invoice_pdf && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadInvoice(invoice.invoice_pdf)}
                        className="hover:bg-ds-gray-subtlest"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="15"
                          viewBox="0 0 14 15"
                          fill="none"
                        >
                          <path
                            d="M7 9.25V2.25"
                            stroke="currentColor"
                            strokeWidth="1.16667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12.25 9.25V11.5833C12.25 11.8928 12.1271 12.1895 11.9083 12.4083C11.6895 12.6271 11.3928 12.75 11.0833 12.75H2.91667C2.60725 12.75 2.3105 12.6271 2.09171 12.4083C1.87292 12.1895 1.75 11.8928 1.75 11.5833V9.25"
                            stroke="currentColor"
                            strokeWidth="1.16667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M4.08203 6.33337L6.9987 9.25004L9.91536 6.33337"
                            stroke="currentColor"
                            strokeWidth="1.16667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              {hasNextPage && (
                <div className="flex justify-center pt-4">
                  <Button
                    variant="outline"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="border border-ds-border-subtler text-ds-text-default hover:bg-ds-gray-subtlest"
                  >
                    {isFetchingNextPage ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <span className="ds-text-body-sm-medium text-ds-text-subtle">
                No invoices found
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Status Modals */}
      {showSuccessModal && (
        <Dialog
          open={showSuccessModal}
          onOpenChange={handleStatusModalOpenChange}
        >
          <DialogContent className="max-w-md">
            <DialogHeader className="text-center">
              <DialogTitle className="ds-text-title-sm-semibold text-ds-text-darker">
                Payment Status
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center py-4">
              <CheckCircle className="w-12 h-12 text-ds-success-default" />
              <span className="mt-3 ds-text-body-md-semibold text-ds-text-darker">
                Subscribed!
              </span>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {showFailureModal && (
        <Dialog
          open={showFailureModal}
          onOpenChange={handleStatusModalOpenChange}
        >
          <DialogContent className="max-w-md">
            <DialogHeader className="text-center">
              <DialogTitle className="ds-text-title-sm-semibold text-ds-text-darker">
                Payment Status
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center py-4">
              <AlertTriangle className="w-12 h-12 text-ds-danger-default" />
              <span className="ds-text-body-md-semibold text-ds-danger-default mt-3">
                Couldn&apos;t subscribe. Please email if you need help
              </span>
            </div>
          </DialogContent>
        </Dialog>
      )}
      <Dialog
        open={isCancelModalOpen}
        onOpenChange={(open) => !open && onCancelModalClose()}
      >
        <DialogContent className="max-w-md">
          <DialogHeader className="text-center">
            <DialogTitle className="ds-text-title-sm-semibold text-ds-text-darker">
              Cancel Subscription
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center gap-4 py-4">
            <AlertTriangle className="w-10 h-10 text-ds-danger-default" />
            <span className="ds-text-body-md-medium text-ds-text-default">
              Are you sure you want to cancel your subscription?
            </span>
            <div className="flex gap-4 justify-center mt-4">
              <Button onClick={onCancelModalClose} variant="outline">
                No
              </Button>
              <Button onClick={cancelSubscription} variant="destructive">
                Yes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Member Modal */}
      <AddMemberModal isOpen={addMemberIsOpen} onClose={addMemberOnClose} />
    </div>
  );
}

export const Billing = () => (
  <Suspense fallback={<div className="animate-pulse h-64" />}>
    <BillingContent />
  </Suspense>
);
